import React, { useState, useEffect } from "react";

import {
  Grid,
  Typography,
  Tooltip,
  ThemeProvider,
  keyframes
} from "@mui/material";

import CalculateIcon from "@mui/icons-material/Calculate";

import { Select } from "@aclymatepackages/atoms";
import mainTheme from "@aclymatepackages/themes";
import { productWeightUnitsTypes } from "@aclymatepackages/products";

import NumberInput from "../../atoms/inputs/NumberInput";

// Define pulse animation
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const weightInputFields = [
  {
    field: "productWeight",
    label: "Product",
    addendum: "+"
  },
  {
    field: "packagingWeight",
    label: "Packaging",
    addendum: "="
  },
  {
    field: "grossWeight",
    label: "Total"
  }
];

const ProductWeightInputRow = ({
  weightObject,
  setWeightObject,
  weightUnit,
  setWeightUnit,
  disabled,
  addendumSize = "h1",
  ...otherProps
}) => {
  const [editHistory, setEditHistory] = useState([]);
  const [shouldPulse, setShouldPulse] = useState(false);

  const onEditWeightObject = (field) => (value) => {
    if (value !== null && value < 0) {
      return;
    }

    const newEditHistory = [
      field,
      ...editHistory.filter((f) => f !== field)
    ].slice(0, 2);
    setEditHistory(newEditHistory);

    const rawUpdatedWeightObject = { ...weightObject, [field]: Number(value) };

    const filledFieldCount = Object.values(weightObject).filter(Boolean).length;
    if (
      !value ||
      filledFieldCount === 0 ||
      value.slice(-1) === "." ||
      (filledFieldCount === 1 && field === Object.keys(weightObject)[0])
    ) {
      return setWeightObject({
        ...otherProps,
        ...weightObject,
        [field]: value
      });
    }

    const calculatedField = Object.keys(weightObject).find(
      (key) => !newEditHistory.includes(key)
    );

    // Trigger pulse animation whenever a calculation happens
    setShouldPulse(true);

    const findUpdatedCalculatedObject = () => {
      const { productWeight, packagingWeight, grossWeight } =
        rawUpdatedWeightObject;

      if (calculatedField === "grossWeight") {
        return {
          ...rawUpdatedWeightObject,
          grossWeight: Number(productWeight) + Number(packagingWeight)
        };
      }

      return {
        ...rawUpdatedWeightObject,
        [calculatedField]:
          grossWeight -
          (calculatedField === "productWeight"
            ? packagingWeight
            : productWeight)
      };
    };

    const weightsUpdateObject = findUpdatedCalculatedObject();

    return setWeightObject({ ...otherProps, ...weightsUpdateObject });
  };

  // Reset pulse animation after it completes
  useEffect(() => {
    if (shouldPulse) {
      const timer = setTimeout(() => {
        setShouldPulse(false);
      }, 1000); // Animation duration

      return () => clearTimeout(timer);
    }
  }, [shouldPulse]);

  const getInputProps = (field) => {
    const isCalculated =
      editHistory.length >= 2 && !editHistory.includes(field);
    const currentCalculatedField =
      editHistory.length >= 2
        ? Object.keys(weightObject).find((key) => !editHistory.includes(key))
        : null;
    const shouldAnimateThisField =
      isCalculated && shouldPulse && field === currentCalculatedField;

    if (isCalculated) {
      return {
        InputProps: {
          endAdornment: (
            <ThemeProvider theme={mainTheme}>
              <Tooltip title="This field is calculated based on the other two weights you entered.">
                <CalculateIcon
                  color="secondary"
                  sx={{
                    mr: 1,
                    fontSize: 30,
                    opacity: 0.7,
                    ...(shouldAnimateThisField && {
                      animation: `${pulseAnimation} 0.6s ease-in-out 2`
                    })
                  }}
                />
              </Tooltip>
            </ThemeProvider>
          )
        }
      };
    }

    return {};
  };

  return (
    <>
      <Typography variant="body2" gutterBottom>
        Enter any 2 out of 3 of your product&apos;s weights (product, packaging or
        total) that you know and we&apos;ll calculate the third.
      </Typography>
      <Grid container spacing={1} alignItems="center" flexWrap="nowrap">
        {weightInputFields.map(({ label, field, addendum }) => (
          <React.Fragment key={field}>
            <Grid item>
              <NumberInput
                label={label}
                value={weightObject[field]}
                setValue={onEditWeightObject(field)}
                {...getInputProps(field)}
                disabled={disabled}
                error={
                  (!weightObject[field] && editHistory.length === 2) ||
                  weightObject[field] < 0
                }
              />
            </Grid>
            {addendum && (
              <Grid item>
                <Typography variant={addendumSize}>{addendum}</Typography>
              </Grid>
            )}
          </React.Fragment>
        ))}
        <Grid item sx={{ minWidth: 100, flexShrink: 0 }}>
          <Select
            label="Unit"
            options={productWeightUnitsTypes.map(({ name, short }) => ({
              label: name,
              value: short
            }))}
            value={weightUnit}
            editValue={setWeightUnit}
            size="small"
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </>
  );
};
export default ProductWeightInputRow;
